import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import React from "react";

import { AccountItemLine } from "./AccountItem";
import { BasicButton } from "../common/buttons/BasicButton.js";
import { NoteUsageCounter } from "../layout/header/NoteUsageCounter.js";
import { instantiateCustomerPortal } from "./../../api/Billing.js";

export function SubscriptionDetails({
  user,
  setShouldShowCancelSubscriptionModal,
}) {
  const { freeMode } = useFlags();

  const renderBillingInfo = (user) => {
    const details = user?.account?.accountDetails;

    const { planName, quantity, billingFrequency, planPrice } =
      details || {};
    if (!planName || !quantity || !billingFrequency || !planPrice) {
      return "Pro";
    }

    const displayPlanName =
      planName === "Scribenote Unlimited"
        ? "Scribenote Pro"
        : planName;

    const timesQty = quantity > 1 ? ` (x${quantity})` : "";
    const per = billingFrequency === "yearly" ? "year" : "month";

    return `${displayPlanName}${timesQty}, recurring ${billingFrequency} (${planPrice} / ${per})`;
  };

  return (
    <>
      <div className="flex-1 divide-y dark:divide-gray-700">
        <AccountItemLine title="Plan Type:">
          <span>{renderBillingInfo(user)}</span>
          {user?.account?.accountDetails?.isCanceled && (
            <strong className="text-sm text-red-400">
              {" "}
              Canceled
            </strong>
          )}
        </AccountItemLine>
        <AccountItemLine title="Upcoming and past invoices">
          <ul
            className="text-xs md:text-sm cursor-pointer hover:text-indigo-500 dark:hover:text-indigo-400"
            onClick={() => {
              mixpanel.track("View upcoming invoices clicked");
              instantiateCustomerPortal();
            }}
          >
            See details
          </ul>

          <Tooltip
            arrow
            placement="top"
            title={
              "This amount may include prorated costs from the current billing cycle. Click Manage Plan to see a detailed breakdown."
            }
          >
            <FontAwesomeIcon
              className="inline-block fill-current text-gray-400 hover:text-indigo-400 ml-2"
              icon={faQuestionCircle}
              size="lg"
            />
          </Tooltip>
        </AccountItemLine>

        {user?.account?.accountType != "unlimited" && !freeMode && (
          <AccountItemLine title="Usage:">
            <NoteUsageCounter small />{" "}
          </AccountItemLine>
        )}

        <div className="text-xs text-gray-500 dark:text-gray-400 pt-1">
          To update billing and subscription details, view past
          invoices and more, please click <strong>Manage Plan</strong>
          .
        </div>
      </div>
      {user?.account?.isTeamsCustomer ? null : (
        <div
          id="accountSubscriptionOptions"
          className="flex flex-row md:flex-col md:items-end items-center justify-center space-x-2 md:space-x-0 md:space-y-2"
        >
          <Tooltip
            title={
              "Change plan, update billing details, update card information, change billing frequency, view past invoices, etc."
            }
            arrow
          >
            <span>
              <BasicButton
                onClick={async () => {
                  mixpanel.track("Manage Plan Clicked");
                  instantiateCustomerPortal();
                }}
              >
                Manage Plan
              </BasicButton>
            </span>
          </Tooltip>

          {!user?.account?.accountDetails?.isCanceled ? (
            <button
              className="text-xs font-medium text-gray-400 transition-all hover:text-red-700 focus:outline-none"
              onClick={() => {
                setShouldShowCancelSubscriptionModal(true);
                mixpanel.track("Cancel Subscription Clicked");
              }}
            >
              Cancel Subscription
            </button>
          ) : null}
        </div>
      )}
    </>
  );
}
